import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Users, Plane, Upload, Loader2, CheckCircle2, AlertCircle, FileAudio, FileVideo, Send, Edit, Sparkles, ClipboardCheck } from "lucide-react";

export default function InstructorConsole() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentModules, setStudentModules] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [openSession, setOpenSession] = useState(null);

  useEffect(() => { if (!loading && !user) navigate("/", { replace: true }); }, [user, loading, navigate]);
  useEffect(() => {
    if (!loading && user && !["instructor", "school_admin", "super_admin"].includes(user.role)) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const refreshAll = async () => {
    try {
      const [s, x] = await Promise.all([
        api.get("/instructor/students"),
        api.get("/instructor/sessions"),
      ]);
      setStudents(s.data); setSessions(x.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { if (user) refreshAll(); }, [user]);

  // Auto-poll sessions in 'analyzing' state every 6s
  useEffect(() => {
    if (!sessions.some((s) => s.ai_status === "analyzing")) return;
    const t = setInterval(refreshAll, 6000);
    return () => clearInterval(t);
  }, [sessions]);

  const pickStudent = async (st) => {
    setSelectedStudent(st);
    try {
      const { data } = await api.get(`/instructor/students/${st.user_id}/modules`);
      setStudentModules(data);
      setShowCreate(true);
    } catch (e) { toast.error("Failed to load student modules"); }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="instructor-page">
      <div className="hud-label" style={{ color: "#FF2A6D" }}>INSTRUCTOR.CONSOLE</div>
      <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight mt-2 text-slate-900">Lesson Delivery Review</h1>
      <p className="mt-2 text-slate-600 max-w-2xl">Pick a student → log the module you delivered live → upload your recording (audio or video) → a structured recap is generated automatically → you review and publish it to the student.</p>

      <div className="mt-10 grid lg:grid-cols-3 gap-4">
        {/* STUDENTS */}
        <div className="instrument-panel p-6">
          <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2"><Users className="w-4 h-4" /> STUDENTS · {students.length}</div>
          <div className="mt-4 space-y-2 max-h-[520px] overflow-auto">
            {students.length === 0 && <div className="text-slate-500 text-sm py-6 text-center">No approved students yet.</div>}
            {students.map((st) => (
              <button
                key={st.user_id}
                onClick={() => pickStudent(st)}
                className="w-full text-left p-3 border border-white/10 hover:border-[var(--brand-primary)]/30 transition flex items-center gap-3 rounded-sm"
                data-testid={`student-${st.user_id}`}
              >
                {st.picture
                  ? <img src={st.picture} alt="" className="w-8 h-8 rounded-sm border border-white/10" />
                  : <div className="w-8 h-8 rounded-sm bg-slate-100 flex items-center justify-center text-[var(--brand-primary)] font-mono font-bold text-sm">{st.name?.[0] || "?"}</div>}
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-sm text-slate-900 truncate" style={{color:"#0F172A"}}>{st.name}</div>
                  <div className="font-mono text-[10px] text-slate-500 truncate">{st.email}</div>
                </div>
                <Plane className="w-3 h-3 text-[var(--brand-primary)] shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* SESSIONS */}
        <div className="instrument-panel p-6 lg:col-span-2">
          <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> DELIVERIES · {sessions.length}</div>
          <div className="mt-4 space-y-2 max-h-[520px] overflow-auto">
            {sessions.length === 0 && (
              <div className="text-slate-500 text-sm py-12 text-center">
                <Plane className="w-8 h-8 mx-auto mb-3 text-[var(--brand-primary)]/40" />
                No sessions yet. Pick a student on the left to create one.
              </div>
            )}
            {sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => setOpenSession(s)}
                className="w-full text-left p-4 border border-white/10 hover:border-[var(--brand-primary)]/30 transition rounded-sm grid grid-cols-12 gap-3 items-center"
                data-testid={`session-${s.session_id}`}
              >
                <div className="col-span-3">
                  <div className="hud-label">{s.flight_date}</div>
                  <div className="font-heading text-base text-slate-900 truncate" style={{color:"#0F172A"}}>{s.student_name}</div>
                </div>
                <div className="col-span-4">
                  <div className="hud-label">MODULE</div>
                  <div className="font-mono text-sm text-slate-700 truncate">{s.lesson_title}</div>
                </div>
                <div className="col-span-2 font-mono text-xs text-slate-600">{s.duration_minutes}m</div>
                <div className="col-span-3 text-right">
                  <StatusBadge status={s.ai_status} published={s.published_to_student} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CREATE DIALOG */}
      <CreateSessionDialog
        open={showCreate} onClose={() => setShowCreate(false)}
        student={selectedStudent} modules={studentModules}
        onCreated={async (s) => { setShowCreate(false); await refreshAll(); setOpenSession(s); }}
      />

      {/* SESSION DETAIL DIALOG */}
      {openSession && (
        <SessionDetailDialog
          session={openSession}
          onClose={() => setOpenSession(null)}
          onRefresh={async () => { await refreshAll(); const upd = (await api.get(`/sessions/${openSession.session_id}`)).data; setOpenSession(upd); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status, published }) {
  if (published) return <span className="hud-label inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 text-green-300"><CheckCircle2 className="w-3 h-3" /> PUBLISHED</span>;
  const map = {
    pending:           ["bg-slate-100 text-slate-600 border-zinc-600", "DRAFT — UPLOAD MEDIA"],
    analyzing:         ["bg-blue-50 text-[var(--brand-primary)] border-blue-200", "ANALYZING…"],
    ready_for_review:  ["bg-[var(--brand-primary)]/15 text-slate-700 border-[var(--brand-primary)]/30", "★ READY TO REVIEW"],
    published:         ["bg-emerald-50 text-green-300 border-emerald-200", "PUBLISHED"],
    failed:            ["bg-red-500/15 text-red-300 border-red-500/40", "ANALYSIS FAILED"],
  };
  const [cls, label] = map[status] || ["", status?.toUpperCase()];
  return <span className={`hud-label inline-flex items-center gap-1 px-2 py-1 border ${cls}`}>{status === "analyzing" && <Loader2 className="w-3 h-3 animate-spin" />}{label}</span>;
}

function CreateSessionDialog({ open, onClose, student, modules, onCreated }) {
  const [form, setForm] = useState({ lesson_id: "", flight_date: "", aircraft: "", departure: "", arrival: "", duration_minutes: 60, instructor_notes: "" });
  useEffect(() => {
    setForm((f) => ({ ...f, flight_date: new Date().toISOString().slice(0, 10) }));
  }, [open]);

  const submit = async () => {
    if (!student || !form.lesson_id || !form.flight_date) return toast.error("Pick module and date");
    try {
      const { data } = await api.post("/instructor/sessions", { ...form, student_id: student.user_id, duration_minutes: parseInt(form.duration_minutes) || 0 });
      toast.success("Delivery logged — now upload your recording");
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!student) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 rounded-sm max-w-2xl" data-testid="create-session-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-slate-900">Log lesson delivery — {student.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="hud-label text-slate-600 mb-2 block">Module delivered</Label>
            <Select value={form.lesson_id} onValueChange={(v) => setForm({ ...form, lesson_id: v })}>
              <SelectTrigger className="bg-slate-50 border-slate-200 rounded-sm" data-testid="module-select"><SelectValue placeholder="Pick a module" /></SelectTrigger>
              <SelectContent className="bg-slate-50 border-slate-200 text-slate-900 rounded-sm">
                {modules.map((m) => (
                  <SelectItem key={m.lesson_id} value={m.lesson_id}>
                    {m.title} {m.score != null ? `· ${m.score}% · ${m.wrong_count} wrong` : (m.status === "in_progress" ? "· in progress" : "· not started")}
                  </SelectItem>
                ))}
                {modules.length === 0 && <SelectItem value="" disabled>Student hasn't started any module yet</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="hud-label mb-1.5 block">Date delivered</Label><Input type="date" value={form.flight_date} onChange={(e) => setForm({ ...form, flight_date: e.target.value })} className="bg-slate-50 border-slate-200 rounded-sm" data-testid="ses-date" /></div>
            <div><Label className="hud-label mb-1.5 block">Duration (minutes)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bg-slate-50 border-slate-200 rounded-sm" data-testid="ses-duration" /></div>
          </div>
          <div>
            <Label className="hud-label mb-1.5 block">Pre-delivery notes (optional, included in the recap)</Label>
            <Textarea value={form.instructor_notes} onChange={(e) => setForm({ ...form, instructor_notes: e.target.value })} className="bg-slate-50 border-slate-200 rounded-sm" rows={3} data-testid="ses-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-slate-200 rounded-sm font-heading tracking-wider">Cancel</Button>
            <Button onClick={submit} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="create-session-btn">Log Delivery</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SessionDetailDialog({ session, onClose, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  const [ytLoading, setYtLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [edit, setEdit] = useState({
    ai_summary: session.ai_summary || "",
    ai_done_well: (session.ai_done_well || []).join("\n"),
    ai_improvements: (session.ai_improvements || []).join("\n"),
    ai_action_items: (session.ai_action_items || []).join("\n"),
    instructor_notes: session.instructor_notes || "",
  });
  useEffect(() => {
    setEdit({
      ai_summary: session.ai_summary || "",
      ai_done_well: (session.ai_done_well || []).join("\n"),
      ai_improvements: (session.ai_improvements || []).join("\n"),
      ai_action_items: (session.ai_action_items || []).join("\n"),
      instructor_notes: session.instructor_notes || "",
    });
  }, [session]);
  const fileInputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/sessions/${session.session_id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — analysis started");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const ingestYoutube = async () => {
    const url = ytUrl.trim();
    if (!url) return;
    setYtLoading(true);
    try {
      await api.post(`/sessions/${session.session_id}/youtube`, { url });
      toast.success("Pulled audio from YouTube — analysis started");
      setYtUrl("");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch that URL");
    } finally { setYtLoading(false); }
  };

  const saveEdits = async () => {
    try {
      await api.post(`/sessions/${session.session_id}/debrief`, {
        summary: edit.ai_summary,
        done_well: edit.ai_done_well.split("\n").map((s) => s.trim()).filter(Boolean),
        improvements: edit.ai_improvements.split("\n").map((s) => s.trim()).filter(Boolean),
        action_items: edit.ai_action_items.split("\n").map((s) => s.trim()).filter(Boolean),
        instructor_notes: edit.instructor_notes,
      });
      toast.success("Saved");
      setEditMode(false);
      await onRefresh();
    } catch (e) { toast.error("Save failed"); }
  };

  const publish = async () => {
    try {
      await api.post(`/sessions/${session.session_id}/publish`);
      toast.success("Published to student");
      await onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 rounded-sm max-w-4xl max-h-[90vh] overflow-auto" data-testid="session-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-slate-900">
            {session.student_name} · {session.lesson_title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div className="instrument-panel p-2"><div className="hud-label">DATE</div><div className="font-mono text-slate-700">{session.flight_date}</div></div>
          <div className="instrument-panel p-2"><div className="hud-label">DURATION</div><div className="font-mono">{session.duration_minutes}m</div></div>
          <div className="instrument-panel p-2"><div className="hud-label">STUDENT</div><div className="font-mono truncate">{session.student_name}</div></div>
          <div className="instrument-panel p-2"><div className="hud-label">TIME</div><div className="font-mono">{session.duration_minutes}m</div></div>
        </div>

        {/* UPLOAD */}
        {!session.media_storage_path && !session.transcribe_path && (
          <div className="instrument-panel p-5 hud-border space-y-5">
            <div>
              <div className="hud-label text-[var(--brand-primary)]">OPTION A — UPLOAD YOUR RECORDING</div>
              <p className="text-slate-600 text-sm mt-2">Accepted: .mp3 .wav .m4a .mp4 .mov · up to 500 MB. Your live delivery is transcribed and turned into a structured recap for the student automatically.</p>
              <input ref={fileInputRef} type="file" accept="audio/*,video/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="upload-input" />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading || ytLoading} className="mt-4 bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Select Media File</>}
              </Button>
            </div>

            <div className="flex items-center gap-3 text-xs text-slate-500">
              <div className="flex-1 h-px bg-[var(--brand-light)]" />
              <span className="hud-label">OR</span>
              <div className="flex-1 h-px bg-[var(--brand-light)]" />
            </div>

            <div>
              <div className="hud-label text-[var(--brand-primary)]">OPTION B — PASTE A YOUTUBE LINK <span className="text-red-600">(experimental)</span></div>
              <p className="text-slate-600 text-sm mt-2">Drop a YouTube URL of your recorded delivery. The audio track is pulled, transcribed, and turned into a recap. <span className="text-[var(--brand-primary)]">Note: YouTube often blocks server-side downloads with HTTP 403 — if it fails, please use Option A and upload the file directly.</span></p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <Input
                  value={ytUrl}
                  onChange={(e) => setYtUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=…"
                  className="bg-slate-50 border-slate-200 rounded-sm font-mono text-sm"
                  data-testid="youtube-url-input"
                  disabled={uploading || ytLoading}
                />
                <Button
                  onClick={ingestYoutube}
                  disabled={!ytUrl.trim() || ytLoading || uploading}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium rounded-sm shrink-0"
                  data-testid="youtube-ingest-btn"
                >
                  {ytLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching…</> : <>Analyse YouTube</>}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* MEDIA PLAYBACK */}
        {session.media_storage_path && (
          <div className="instrument-panel p-4">
            <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2">
              {session.media_type === "video" ? <FileVideo className="w-4 h-4" /> : <FileAudio className="w-4 h-4" />}
              {session.media_filename} · {(session.media_size_bytes / 1024 / 1024).toFixed(1)} MB
            </div>
            {session.media_type === "video"
              ? <video controls src={fileUrl(session.media_storage_path)} className="mt-3 w-full max-h-72 bg-black" />
              : <audio controls src={fileUrl(session.media_storage_path)} className="mt-3 w-full" />}
          </div>
        )}

        {/* YOUTUBE SOURCE */}
        {!session.media_storage_path && session.youtube_url && (
          <div className="instrument-panel p-4" data-testid="youtube-source-card">
            <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2">
              ★ SOURCE · YOUTUBE
            </div>
            <a href={session.youtube_url} target="_blank" rel="noreferrer" className="block mt-2 font-heading text-lg text-[var(--brand-primary)] hover:text-[var(--brand-primary)] truncate">
              {session.youtube_title || session.youtube_url}
            </a>
            {session.youtube_uploader && <div className="hud-label mt-1">UPLOADER · {session.youtube_uploader}</div>}
          </div>
        )}

        {/* DEBRIEF ANALYSIS */}
        {session.transcribe_path && session.ai_status === "analyzing" && (
          <div className="instrument-panel p-6 hud-border text-center">
            <Loader2 className="w-8 h-8 text-[var(--brand-primary)] animate-spin mx-auto" />
            <div className="font-heading text-xl mt-3 text-slate-700">BUILDING YOUR RECAP</div>
            {(() => {
              const p = session.ai_progress;
              const stageLabel = {
                loading_audio: "FETCHING AUDIO",
                splitting:     "PREPARING CHUNKS",
                transcribing:  "TRANSCRIBING",
                summarizing:   "BUILDING DEBRIEF",
              }[p?.stage] || "TRANSCRIBING · BUILDING DEBRIEF";
              return (
                <>
                  <div className="hud-label mt-2 text-[var(--brand-primary)]">{stageLabel}</div>
                  {p?.detail && <div className="font-mono text-xs text-slate-700 mt-2">{p.detail}</div>}
                  {p?.total > 0 && (
                    <div className="max-w-md mx-auto mt-4">
                      <div className="h-1.5 bg-slate-100 overflow-hidden">
                        <div className="h-full bg-amber-500 transition-[width]" style={{ width: `${Math.round((p.done / p.total) * 100)}%` }} />
                      </div>
                      <div className="font-mono text-[10px] text-slate-500 mt-1">{p.done} / {p.total} CHUNKS · {Math.round((p.done / p.total) * 100)}%</div>
                    </div>
                  )}
                  {p?.ts && <div className="font-mono text-[10px] text-slate-500 mt-3">last update {new Date(p.ts).toLocaleTimeString()}</div>}
                </>
              );
            })()}
          </div>
        )}
        {session.ai_status === "failed" && (
          <div className="instrument-panel p-5 border border-red-500/40 space-y-3">
            <div className="hud-label text-red-400 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> ANALYSIS FAILED</div>
            <div className="text-sm text-slate-700 break-words">{session.ai_error || "Unknown error"}</div>
            <Button size="sm" onClick={async () => {
              try {
                await api.post(`/sessions/${session.session_id}/reanalyze`);
                toast.success("Re-analysis started");
                await onRefresh();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed to restart"); }
            }} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="reanalyze-btn">
              <Sparkles className="w-3 h-3 mr-1" /> Re-Analyze
            </Button>
          </div>
        )}
        {(session.ai_status === "ready_for_review" || session.ai_status === "published") && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="hud-label text-[var(--brand-primary)] flex items-center gap-2"><Sparkles className="w-4 h-4" /> LESSON RECAP</div>
              <div className="flex gap-2">
                {!session.published_to_student && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)} className="border-blue-200 text-[var(--brand-primary)] hover:bg-blue-50 rounded-sm font-heading tracking-wider" data-testid="edit-debrief-btn">
                      <Edit className="w-3 h-3 mr-1" /> {editMode ? "Cancel" : "Edit"}
                    </Button>
                    {editMode && <Button size="sm" onClick={saveEdits} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="save-debrief-btn">Save</Button>}
                    {!editMode && <Button size="sm" onClick={publish} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] font-medium rounded-sm" data-testid="publish-btn"><Send className="w-3 h-3 mr-1" /> Publish to Student</Button>}
                  </>
                )}
              </div>
            </div>

            <DebriefSection title="Instructor Notes (private — not shown to student until published)" color="#A78BFA">
              {editMode
                ? <Textarea value={edit.instructor_notes} onChange={(e) => setEdit({ ...edit, instructor_notes: e.target.value })} rows={3} className="bg-slate-50 border-slate-200 rounded-sm" placeholder="Personal notes you want appended to the debrief…" data-testid="instructor-notes-edit" />
                : (session.instructor_notes
                    ? <p className="text-slate-800 leading-relaxed whitespace-pre-wrap" data-testid="instructor-notes-view">{session.instructor_notes}</p>
                    : <div className="text-slate-500 text-sm italic">(no instructor notes yet — click Edit to add)</div>)}
            </DebriefSection>

            <DebriefSection title="Lesson Recap" color="#F5A623">
              {editMode
                ? <Textarea value={edit.ai_summary} onChange={(e) => setEdit({ ...edit, ai_summary: e.target.value })} rows={5} className="bg-slate-50 border-slate-200 rounded-sm" />
                : <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{session.ai_summary}</p>}
            </DebriefSection>

            <DebriefSection title="Key Takeaways" color="#22C55E">
              <BulletList items={session.ai_done_well || []} editing={editMode} value={edit.ai_done_well} onChange={(v) => setEdit({ ...edit, ai_done_well: v })} />
            </DebriefSection>

            <DebriefSection title="Watch Out For" color="#FF2A6D">
              <BulletList items={session.ai_improvements || []} editing={editMode} value={edit.ai_improvements} onChange={(v) => setEdit({ ...edit, ai_improvements: v })} />
            </DebriefSection>

            <DebriefSection title="Next Steps" color="#00E0FF">
              <BulletList items={session.ai_action_items || []} editing={editMode} value={edit.ai_action_items} onChange={(v) => setEdit({ ...edit, ai_action_items: v })} />
            </DebriefSection>

            {session.ai_transcript && (
              <details className="instrument-panel p-4">
                <summary className="hud-label text-[var(--brand-primary)] cursor-pointer">VERBATIM TRANSCRIPT (CLICK TO EXPAND)</summary>
                <div className="mt-3 text-slate-700 text-sm leading-relaxed whitespace-pre-wrap font-mono">{session.ai_transcript}</div>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DebriefSection({ title, color, children }) {
  return (
    <div className="instrument-panel p-4 border" style={{ borderColor: color + "40" }}>
      <div className="hud-label" style={{ color }}>{title.toUpperCase()}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function BulletList({ items, editing, value, onChange }) {
  if (editing) {
    return <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={Math.max(4, value.split("\n").length)} className="bg-slate-50 border-slate-200 rounded-sm font-mono text-sm" placeholder="One bullet per line" />;
  }
  if (!items.length) return <div className="text-slate-500 text-sm italic">(none)</div>;
  return (
    <ul className="space-y-1.5">
      {items.map((it, i) => <li key={i} className="text-slate-800 leading-relaxed flex gap-2"><span className="text-[var(--brand-primary)] shrink-0">·</span> <span>{it}</span></li>)}
    </ul>
  );
}
